import {
    buildLocalBusinessSchema,
    buildProductSchema,
    buildBreadcrumbSchema,
    buildFAQSchema,
    buildSpeakableSchema,
} from "../../../shared/seo/schemas";
import type { SeoHeadProps } from "../../../shared/seo/SeoHead";
import { SITE_BASE } from "../../../shared/seo/siteUrl";

const BASE = SITE_BASE;
const URL = `${BASE}/products/pom-poms`;

const FAQS = [
    { q: "What is the minimum order quantity for pom poms?", a: "Our standard MOQ is 50 pieces per design and colour. Custom-coloured or branded orders begin at 200 pieces. Bulk discounts apply above 500 pieces." },
    { q: "What materials do you use for pom poms?", a: "We manufacture pom poms in wool, acrylic, cotton, blends and synthetic faux-fur variants. Materials are cruelty-free where applicable and OEKO-TEX compliant on request." },
    { q: "Do you export pom poms internationally from India?", a: "Yes — we export to 50+ countries including USA, UK, EU, Germany, France, UAE and Australia. Full documentation, ISO certification and export packaging provided." },
    { q: "What sizes of pom poms do you manufacture?", a: "Eight standard sizes from 1cm up to 10cm. Custom sizes available for bulk orders above 500 pieces." },
    { q: "Can you supply pom poms with snap buttons?", a: "Yes — snap-on pom poms are our most popular variant. KAM brand snaps in nickel, gold or custom finishes. Ideal for interchangeable beanie pom poms." },
];

export const PomPomsSeo: SeoHeadProps = {
    title: "Pom Poms Manufacturer Delhi | Wholesale & Export India | Arte Craft India",
    description:
        "Premium handmade pom poms manufacturer in Delhi, India. 200+ colours, wool/acrylic/cotton, sizes 1cm–10cm. Snap-on, sew-on, hat pom poms. Wholesale & export. MOQ 50 pcs. Free samples.",
    keywords:
        "pom poms manufacturer Delhi, handmade pom poms India, wholesale pom poms, custom pom poms, wool pom poms, acrylic pom poms, pom poms for hats, bulk pom poms, hat pompom supplier, snap on pom poms, sew on pom poms, fluffy pom poms India, pom pom exporter India, pom pom factory Delhi",
    canonical: URL,
    author: "Arte Craft India",
    robots: "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1",

    ogType: "product",
    ogUrl: URL,
    ogSiteName: "Arte Craft India",
    ogImage: `${BASE}/images/pom-poms-og.jpg`,
    ogImageAlt: "Handmade pom poms in multiple colors and materials by Arte Craft India Delhi",
    ogLocale: "en_IN",
    ogLocaleAlternates: ["en_US", "en_GB", "en_AU", "en_AE"],

    twitterCard: "summary_large_image",
    twitterSite: "@ArteCraftIndia",
    twitterCreator: "@ArteCraftIndia",
    twitterImage: `${BASE}/images/pom-poms-twitter.jpg`,
    twitterImageAlt: "Assorted handmade pom poms from Arte Craft India Delhi manufacturer",
    twitterLabel1: "Material",
    twitterData1: "Wool · Acrylic · Cotton · Faux Fur",
    twitterLabel2: "MOQ",
    twitterData2: "50 pieces — bulk discount from 500 pcs",

    aiBots: true,

    geoRegion: "IN-DL",
    geoPlacename: "Badarpur, New Delhi, India",
    geoPosition: "28.5045;77.2855",

    dublinCore: {
        title: "Pom Poms Manufacturer Delhi | Wholesale & Export India",
        creator: "Arte Craft India",
        subject: "Pom Poms, Manufacturing, Wholesale, Export, Delhi, India",
        description: "Premium handmade pom poms manufacturer in Delhi for wholesale & export.",
        publisher: "Arte Craft India Pvt Ltd, Delhi",
        date: "2026-05-26",
        type: "Service, Product Catalog, Manufacturer Profile",
        language: "en",
        rights: "© 2026 Arte Craft India Pvt Ltd. All rights reserved.",
    },

    hreflangs: [
        { lang: "en-IN", url: URL },
        { lang: "en-US", url: URL },
        { lang: "en-GB", url: URL },
        { lang: "en-AU", url: URL },
        { lang: "en-AE", url: URL },
        { lang: "en", url: URL },
        { lang: "x-default", url: URL },
    ],

    productBrand: "Arte Craft India",
    productAvailability: "in stock",
    productCondition: "new",
    productPriceAmount: "10.00",
    productPriceCurrency: "INR",
    productMaterial: "Wool · Acrylic · Cotton · Faux Fur blend",
    productColor: "200+ shades",
    productSize: "1cm · 2cm · 3cm · 4cm · 5cm · 6cm · 8cm · 10cm",
    productCategory: "Apparel & Accessories > Clothing Accessories > Pom Poms",

    themeColor: "#1B4F72",

    jsonLd: [
        buildLocalBusinessSchema(),
        buildProductSchema({
            name: "Pom Poms — Handmade, Wholesale & Export",
            sku: "AC-POMPOM-001",
            canonicalUrl: URL,
            imageSlug: "pom-poms",
            description:
                "Premium handmade pom poms manufactured in Delhi, India. 200+ colours, sizes 1cm–10cm. Snap-on, sew-on, hat pom poms. Wholesale & export.",
            material: "Wool · Acrylic · Cotton · Faux Fur blend",
            color: "200+ shades",
            sizes: ["1cm", "2cm", "3cm", "4cm", "5cm", "6cm", "8cm", "10cm"],
            category: "Apparel & Accessories > Clothing Accessories > Pom Poms",
            lowPrice: "10",
            highPrice: "120",
        }),
        buildBreadcrumbSchema([
            { name: "Home", url: `${BASE}/` },
            { name: "Products", url: `${BASE}/products` },
            { name: "Pom Poms", url: URL },
        ]),
        buildFAQSchema(FAQS),
        buildSpeakableSchema(URL, "Pom Poms Manufacturer Delhi | Wholesale & Export India", "pom-poms"),
    ],
};
